import { api } from '@/lib/api';

// Options every REPORT query shares. Reports are cached on the server for 60s
// and evicted when an order or spend row arrives, so a page can keep showing
// what it has: stale-for-five-minutes is a number that was right a moment ago,
// a spinner is no number at all. placeholderData keeps the previous result on
// screen while a new window loads, instead of blanking the grid.
export const REPORT_QUERY = {
  staleTime: 5 * 60_000,
  refetchOnWindowFocus: false,
  placeholderData: (prev) => prev,
};

// One record's page (an order, a customer): cached like a report, but it never
// holds the previous record on screen while the next one loads. The detail
// page stays mounted when its :id changes, so the previous answer would be
// another customer's numbers under this customer's name.
export const RECORD_QUERY = { ...REPORT_QUERY, placeholderData: undefined };

// ── One definition per report's primary query ────────────────────────────────
// A page and the prefetch that warms it (lib/route-preload: a sidebar hover,
// the boot of a fresh load, the idle warm-up) must name the SAME key and ask
// the SAME question, or the prefetch lands in a cache entry the page never
// reads. So both import these rather than each writing its own.
const get = (url, config) => async () => (await api.get(url, config)).data;

export const commandCenterQuery = (days) => ({
  ...REPORT_QUERY,
  queryKey: ['growth', 'command-center', days],
  queryFn: get('/api/commerce/growth/command-center', { params: { days } }),
});
export const weekMovedQuery = (mode) => ({
  ...REPORT_QUERY,
  queryKey: ['growth', 'week-moved', mode],
  queryFn: get('/api/commerce/growth/week-moved', { params: { mode } }),
});
export const budgetPlanQuery = (mode, budget) => ({
  ...REPORT_QUERY,
  // Re-asked as a budget is typed, and once more when the page seeds it from
  // last week's spend: the plan card keeps its answer, the page is not dimmed.
  meta: { dimPage: false },
  queryKey: ['growth', 'budget-plan', mode, budget],
  queryFn: get('/api/commerce/growth/budget-plan', {
    params: { mode, ...(Number(budget) > 0 ? { budget } : {}) },
  }),
});
export const growthActionsQuery = () => ({
  ...REPORT_QUERY,
  queryKey: ['growth', 'actions'],
  // 60s, not the client's default 15: the feed reviews every campaign and a
  // cold build legitimately takes seconds. When it did exceed the default
  // the request aborted and this page rendered NOTHING - no data, no error,
  // no retry - which reads as "the product is broken", silently.
  queryFn: get('/api/commerce/growth/actions', { timeout: 60_000 }),
});
export const storeInsightsQuery = (days) => ({
  ...REPORT_QUERY,
  queryKey: ['insights', 'store', days],
  queryFn: get(`/api/insights/store?days=${Number(days) || 90}`),
  staleTime: 5 * 60 * 1000,
});
export const customerValueQuery = () => ({
  ...REPORT_QUERY,
  queryKey: ['growth', 'customer-value'],
  queryFn: get('/api/commerce/growth/customer-value'),
});
// The predicted value of new customers (api customerValueModel.reportSection):
// by channel and campaign once the model is ready, else its learning progress.
export const predictedValueQuery = () => ({
  ...REPORT_QUERY,
  queryKey: ['growth', 'predicted-value'],
  queryFn: get('/api/commerce/growth/predicted-value'),
});
export const purchasesQuery = (days) => ({
  ...REPORT_QUERY,
  queryKey: ['purchases', days],
  // campaign_days = the same picker: the campaign table used to read a fixed
  // 90 days under a header that said 28.
  queryFn: get(`/api/purchases?days=${days}&campaign_days=${days}`),
  staleTime: 5 * 60 * 1000,
});
export const placesQuery = (days) => ({
  ...REPORT_QUERY,
  queryKey: ['opportunity-places', days],
  queryFn: get(`/api/opportunities/places?days=${days}`),
  staleTime: 5 * 60 * 1000,
});
// `range` ({ from, to }) asks for exact dates, Yesterday included; without it
// the window is the last `days` days to today, as before.
export const campaignsQuery = (days, level, range = null) => ({
  ...REPORT_QUERY,
  queryKey: range?.from && range?.to ? ['campaigns', days, level, range.from, range.to] : ['campaigns', days, level],
  queryFn: get('/api/campaigns', { params: range?.from && range?.to ? { days, level, from: range.from, to: range.to } : { days, level } }),
});
export const leaksQuery = (days) => ({
  ...REPORT_QUERY,
  queryKey: ['growth-leaks', days],
  queryFn: get('/api/commerce/growth/leaks', { params: { days } }),
});
export const channelRolesQuery = (days) => ({
  ...REPORT_QUERY,
  queryKey: ['growth', 'channel-roles', days],
  queryFn: get('/api/commerce/growth/channel-roles', { params: { days } }),
});
export const funnelsQuery = (days) => ({
  ...REPORT_QUERY,
  queryKey: ['growth', 'funnels', days],
  queryFn: get('/api/commerce/growth/funnels', { params: { days } }),
});
export const studioQuery = (win) => ({
  ...REPORT_QUERY,
  queryKey: ['signal-studio', win],
  queryFn: get('/api/commerce/studio', { params: { days: win } }),
});
// Analytics & Insights: /api/commerce/insights/<kind> on the page's window,
// credit model and customer segment.
export const insightQuery = (kind, { days, range, model, segment = 'all' }, extra = null) => {
  const custom = range && range.from && range.to;
  const params = { ...(custom ? { from: range.from, to: range.to, model } : { days, model }), ...(segment !== 'all' ? { segment } : {}), ...(extra || {}) };
  return {
    ...REPORT_QUERY,
    queryKey: ['commerce', 'insights', kind, custom ? `${range.from}_${range.to}` : days, model, segment, extra ? JSON.stringify(extra) : ''],
    queryFn: get(`/api/commerce/insights/${kind}`, { params }),
    staleTime: 30_000,
  };
};
